import React, { Suspense, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SpinnerLoader, SpinnerLoaderSizes } from '@yme/atoms/spinner-loader';
import { CONFIGURATOR_PARAM_NAME, CONFIGURATOR_PARAM_VALUE_ON, CONFIGURATOR_SOURCE_PARAM_VALUE, PCM_CODE, PRODUCT_YEAR, SOURCE_PARAM_NAME, } from '@yme/common/constants';
import ErrorBoundary from '@yme/common/utils/ErrorBoundary';
import { useMenuHeight } from '@yme/hooks/useMenuHeight';
import { setIsModalHide } from '@yme/modals/actions';
import { getModalData } from '@yme/modals/selectors';
import { CTA_ENDPOINT_TYPE } from '@yme/pdp-components/ConfiguratorModal/constants';
import { getAttributeWithGroupingProducts } from '@yme/pdp-components/ProductCard';
import { getConfiguratorTestRideMap } from '@yme/pdp-components/ProductCard/redux/selectors';
import { getCommercetoolsProjectKey, getLocale } from '@yme/selectors/common';
import { getAttribute } from '@yme/selectors/products';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { ConfContainer } from '../ConfContainer';
import { getProjectKey, getTestRideLinksEndpoints, prepareLocale } from './helpers';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line import/no-unresolved
const YMEConfigurator = React.lazy(() => import('Remote/YMEConfigurator'));
export function ConfiguratorModalWindow() {
    const dispatch = useDispatch();
    const history = useHistory();
    const menuHeight = useMenuHeight();
    const ctaEndpoints = [];
    const modalData = useSelector(getModalData);
    const hideModal = useCallback(() => {
        const queryParams = new URLSearchParams(history.location.search);
        if (queryParams.get(CONFIGURATOR_PARAM_NAME) === CONFIGURATOR_PARAM_VALUE_ON) {
            history.replace({
                search: '',
            });
        }
        dispatch(setIsModalHide());
    }, [dispatch, history]);
    useEffect(() => {
        setDigitalData(DigitalDataMutation.CONFIGURATOR_START, null);
        const queryParams = new URLSearchParams(history.location.search);
        queryParams.delete(CONFIGURATOR_PARAM_NAME);
        queryParams.set(CONFIGURATOR_PARAM_NAME, CONFIGURATOR_PARAM_VALUE_ON);
        history.replace({
            hash: history.location.hash,
            search: queryParams.toString(),
        });
    }, [history]);
    // to handle edge-case when page scrollY doesn't reach sticky header appearing
    // https://support.yamnet.com/browse/B2C-19874
    useEffect(() => {
        const { scrollY } = window;
        if (scrollY > 0 && scrollY < menuHeight) {
            document.documentElement.scrollTop = 0;
        }
    }, [menuHeight]);
    useEffect(() => {
        const listenHistory = history.listen((_, action) => {
            const queryParams = new URLSearchParams(history.location.search);
            if (action === 'POP' &&
                queryParams.get(CONFIGURATOR_PARAM_NAME) !== CONFIGURATOR_PARAM_VALUE_ON) {
                dispatch(setIsModalHide());
            }
        });
        return () => listenHistory();
    }, [dispatch, history]);
    const productPCMCode = useSelector(getAttributeWithGroupingProducts(PCM_CODE));
    const productYear = useSelector(getAttribute(PRODUCT_YEAR));
    const commercetoolsProjectKey = useSelector(getCommercetoolsProjectKey);
    const configuratorTestRideMap = useSelector(getConfiguratorTestRideMap);
    const locale = useSelector(getLocale);
    const linkForLeadForm = modalData?.configuratorLeadFormPagePath;
    if (configuratorTestRideMap) {
        try {
            const ctaEndpointsForTestRide = getTestRideLinksEndpoints(configuratorTestRideMap);
            ctaEndpointsForTestRide.forEach((endpoint) => {
                ctaEndpoints.push(endpoint);
            });
        }
        catch (error) { }
    }
    if (linkForLeadForm) {
        const url = new URL(linkForLeadForm, window.location.origin);
        url.searchParams.set(SOURCE_PARAM_NAME, CONFIGURATOR_SOURCE_PARAM_VALUE);
        ctaEndpoints.push({
            type: CTA_ENDPOINT_TYPE.quotation,
            url: url.toString(),
        });
    }
    const data = {
        modelId: `${productYear}-${productPCMCode}`,
        // configurator works only with prod units
        projectKey: getProjectKey(commercetoolsProjectKey),
        // configurator not works with EU locale
        locale: prepareLocale(locale, commercetoolsProjectKey),
        environment: 'prod',
        ctaEndpoints,
    };
    return productPCMCode ? (React.createElement(ErrorBoundary, { key: `${productPCMCode}_errorBoundary` },
        React.createElement(Suspense, { fallback: React.createElement(SpinnerLoader, { isLoading: true, size: SpinnerLoaderSizes.SMALL }) },
            React.createElement(ConfContainer, { key: `${productPCMCode}_container` },
                React.createElement(YMEConfigurator, { key: `${productPCMCode}_configurator`, ...data, onExit: hideModal }))))) : null;
}
